/**
 * Performance Monitoring Utility
 * 
 * Tracks and reports application performance metrics
 * Requirements: 19.1, 19.2
 */

export interface PerformanceMetrics {
  loadTime: number;
  firstContentfulPaint: number;
  timeToInteractive: number;
  audioProcessingLatency: number;
}

class PerformanceMonitor {
  private metrics: Partial<PerformanceMetrics> = {};
  private audioLatencyMeasurements: number[] = [];

  /**
   * Initialize performance monitoring
   */
  initialize(): void {
    if (typeof window === 'undefined' || !window.performance) {
      console.warn('Performance API not available');
      return;
    }

    // Monitor page load performance
    window.addEventListener('load', () => {
      this.measureLoadPerformance();
    });

    // Use PerformanceObserver for paint timing
    if ('PerformanceObserver' in window) {
      try {
        const paintObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.name === 'first-contentful-paint') {
              this.metrics.firstContentfulPaint = entry.startTime;
              console.log(`First Contentful Paint: ${entry.startTime.toFixed(2)}ms`);
            }
          }
        });
        paintObserver.observe({ entryTypes: ['paint'] });
      } catch (e) {
        console.warn('PerformanceObserver not supported', e);
      }
    }
  }

  /**
   * Measure page load performance
   */
  private measureLoadPerformance(): void {
    const perfData = window.performance.timing;
    const loadTime = perfData.loadEventEnd - perfData.navigationStart;
    
    this.metrics.loadTime = loadTime;
    
    console.log('Performance Metrics:', {
      loadTime: `${loadTime}ms`,
      domContentLoaded: `${perfData.domContentLoadedEventEnd - perfData.navigationStart}ms`,
      domInteractive: `${perfData.domInteractive - perfData.navigationStart}ms`,
    });

    // Check if load time meets requirement (< 2 seconds)
    if (loadTime > 2000) {
      console.warn(`Load time (${loadTime}ms) exceeds target of 2000ms`);
    }
  }

  /**
   * Measure audio processing latency
   */
  measureAudioLatency(startTime: number, endTime: number): void {
    const latency = endTime - startTime;
    this.audioLatencyMeasurements.push(latency);
    
    // Keep only last 100 measurements
    if (this.audioLatencyMeasurements.length > 100) {
      this.audioLatencyMeasurements.shift();
    }

    // Calculate average latency
    const avgLatency = this.audioLatencyMeasurements.reduce((a, b) => a + b, 0) / 
                       this.audioLatencyMeasurements.length;
    
    this.metrics.audioProcessingLatency = avgLatency;

    // Warn if latency exceeds requirement (< 100ms)
    if (latency > 100) {
      console.warn(`Audio processing latency (${latency.toFixed(2)}ms) exceeds target of 100ms`);
    }
  }

  /**
   * Mark a custom performance measurement
   */
  mark(name: string): void {
    if (window.performance && window.performance.mark) {
      window.performance.mark(name);
    }
  }

  /**
   * Measure time between two marks
   */
  measure(name: string, startMark: string, endMark: string): number | null {
    if (window.performance && window.performance.measure) {
      try {
        window.performance.measure(name, startMark, endMark);
        const measure = window.performance.getEntriesByName(name)[0];
        return measure ? measure.duration : null;
      } catch (e) {
        console.warn('Performance measurement failed', e);
        return null;
      }
    }
    return null;
  }

  /**
   * Get current metrics
   */
  getMetrics(): Partial<PerformanceMetrics> {
    return { ...this.metrics };
  }

  /**
   * Get average audio processing latency
   */
  getAverageAudioLatency(): number {
    return this.metrics.audioProcessingLatency || 0;
  }

  /**
   * Clear all measurements
   */
  clear(): void {
    this.metrics = {};
    this.audioLatencyMeasurements = [];
    if (window.performance && window.performance.clearMarks) {
      window.performance.clearMarks();
      window.performance.clearMeasures();
    }
  }

  /**
   * Log performance report
   */
  logReport(): void {
    console.group('Performance Report');
    console.log('Load Time:', this.metrics.loadTime ? `${this.metrics.loadTime}ms` : 'N/A');
    console.log('First Contentful Paint:', this.metrics.firstContentfulPaint ? 
                `${this.metrics.firstContentfulPaint.toFixed(2)}ms` : 'N/A');
    console.log('Average Audio Latency:', this.metrics.audioProcessingLatency ? 
                `${this.metrics.audioProcessingLatency.toFixed(2)}ms` : 'N/A');
    console.groupEnd();
  }
}

// Export singleton instance
export const performanceMonitor = new PerformanceMonitor();
